import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, downloadWithToast, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  CaretLeft, CaretRight, Upload, DownloadSimple, FilePdf, FileXls, CalendarBlank, ArrowsClockwise,
  CheckSquare, ArrowSquareOut, MapPin, CalendarCheck,
} from "@phosphor-icons/react";
import { AdminOnly } from "@/pages/hr/Payroll";
import AttendanceCellEditor from "@/components/hr/AttendanceCellEditor";
import HolidaysDialog from "@/components/hr/HolidaysDialog";
import SitesDialog from "@/components/hr/SitesDialog";
import { LeaveRequestsDialog } from "@/components/hr/LeaveRequestsPanel";
import { STATUS_STYLE, MONTHS_LONG as MONTHS, fmtN, fmt12 } from "@/components/hr/attendanceStatus";

export default function AttendanceRegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [reg, setReg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // {empIdx, day, rect}
  const [holidaysOpen, setHolidaysOpen] = useState(false);
  const [sitesOpen, setSitesOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  const loadPendingLeave = useCallback(async () => {
    try { const { data } = await api.get("/payroll/leave-requests", { params: { status: "pending", limit: 1 } }); setPendingLeave(data.counts?.pending || 0); } catch { /* badge only */ }
  }, []);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/payroll/attendance/${ym.year}/${ym.month}`);
      setReg(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }, [ym]);

  useEffect(() => { if (user?.role === "admin") { load(); loadPendingLeave(); } }, [load, loadPendingLeave, user]);

  const rows = useMemo(() => {
    if (!reg) return [];
    const t = q.trim().toLowerCase();
    return reg.employees.map((e, idx) => ({ e, idx })).filter(({ e }) => !t || [e.employee.name, e.employee.emp_code, e.employee.designation].some((x) => (x || "").toLowerCase().includes(t)));
  }, [reg, q]);

  const dim = reg?.days_in_month || 30;
  const dayHeads = useMemo(() => Array.from({ length: dim }, (_, i) => {
    const d = new Date(ym.year, ym.month - 1, i + 1);
    const iso = `${ym.year}-${String(ym.month).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
    return { d: i + 1, wd: d.toLocaleDateString("en-IN", { weekday: "short" }).slice(0, 2), off: (reg?.settings?.weekly_off_days || [6]).includes((d.getDay() + 6) % 7), holiday: reg?.holidays?.find((h) => h.date === iso)?.name };
  }), [dim, ym, reg]);

  if (user?.role !== "admin") return <AdminOnly />;

  const shift = (delta) => {
    const d = new Date(ym.year, ym.month - 1 + delta, 1);
    setYm({ year: d.getFullYear(), month: d.getMonth() + 1 });
    setEditing(null);
  };

  const tag = `${MONTHS[ym.month - 1].slice(0, 3)}-${ym.year}`;
  const base = `/payroll/attendance/${ym.year}/${ym.month}`;

  const bulk = async (action, extra = {}) => {
    try {
      const { data } = await api.post(`${base}/bulk`, { action, ...extra });
      setReg(data);
      toast.success(`${data.changed_cells} cell${data.changed_cells === 1 ? "" : "s"} updated`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReg(data);
      const im = data.import;
      toast.success(`Imported ${im.cells} day entries for ${im.matched_employees} employees (${im.format} format)`);
      if (im.unmatched?.length) toast.warning(`Unmatched rows: ${im.unmatched.slice(0, 5).join(", ")}${im.unmatched.length > 5 ? "…" : ""}`, { duration: 8000 });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const applyToRun = async () => {
    if (!reg?.run) return toast.error(`No payroll run for ${MONTHS[ym.month - 1]} ${ym.year} yet — import the salary register first.`);
    if (!window.confirm(`Sync attendance into the ${MONTHS[ym.month - 1]} ${ym.year} payroll run?\n\nPaid days, leaves, late marks and half-days will be written to every employee, earnings reset to the full monthly rate, and an "Attendance Deduction" line added for LOP days. Manual edits to those fields in the run will be overwritten.`)) return;
    try {
      const { data } = await api.post(`${base}/apply-to-run`);
      toast.success(`Synced ${data.updated} employees · Attendance deduction ₹${Number(data.run.totals.attendance_deduction || 0).toLocaleString("en-IN")} · Net ₹${Number(data.run.totals.net_payable || 0).toLocaleString("en-IN")}`, { duration: 7000 });
      if (data.skipped?.length) toast.warning(`No attendance yet for: ${data.skipped.slice(0, 4).join(", ")}${data.skipped.length > 4 ? "…" : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onDaySaved = (empIdx, payload) => {
    setReg((r) => {
      const employees = r.employees.map((e, i) => (i === empIdx ? { ...e, days: payload.days, summary: payload.summary } : e));
      const totals = employees.reduce((a, e) => { a.paid_days += e.summary.paid_days; a.leaves += e.summary.leaves; a.late_marks += e.summary.late_marks; a.half_days += e.summary.half_days; a.unmarked += e.summary.unmarked; return a; }, { paid_days: 0, leaves: 0, late_marks: 0, half_days: 0, unmarked: 0, employees: employees.length });
      return { ...r, employees, totals };
    });
  };

  const t = reg?.totals || {};
  return (
    <div className="space-y-3 p-4" data-testid="attendance-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <button className="text-[11px] text-slate-500 hover:text-[#EA580C] flex items-center gap-1 mb-1" onClick={() => navigate("/payroll")} data-testid="attendance-back">← Payroll</button>
          <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">Attendance Register</h1>
          <div className="text-[11.5px] text-slate-500">
            Shift {fmt12(reg?.settings?.shift_in)} – {fmt12(reg?.settings?.shift_out)} · late beyond {reg?.settings?.late_grace_min ?? 30} min = half-day
            {reg?.settings?.late_tolerance_count ? ` (first ${reg.settings.late_tolerance_count} forgiven)` : ""} · Sundays &amp; holidays paid · Leaves/Absent are LOP · click any cell to mark
          </div>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-sm px-1 py-1" data-testid="attendance-month-nav">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => shift(-1)} data-testid="attendance-prev-month"><CaretLeft size={14} /></Button>
          <select value={ym.month} onChange={(e) => { setYm({ ...ym, month: Number(e.target.value) }); setEditing(null); }} className="h-7 text-[12px] font-semibold bg-transparent px-1" data-testid="attendance-month-select">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input type="number" value={ym.year} onChange={(e) => { setYm({ ...ym, year: Number(e.target.value) }); setEditing(null); }} className="h-7 w-16 text-[12px] font-semibold bg-transparent px-1 font-mono-num" data-testid="attendance-year-input" />
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => shift(1)} data-testid="attendance-next-month"><CaretRight size={14} /></Button>
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="attendance-kpis">
        <Kpi label="Employees" value={t.employees ?? "—"} />
        <Kpi label="Paid days" value={fmtN(t.paid_days)} sub={`of ${(t.employees || 0) * dim}`} />
        <Kpi label="Leaves (LOP)" value={fmtN(t.leaves)} tone={t.leaves ? "rose" : ""} />
        <Kpi label="Late marks" value={fmtN(t.late_marks)} tone={t.late_marks ? "amber" : ""} />
        <Kpi label="Half-days" value={fmtN(t.half_days)} tone={t.half_days ? "amber" : ""} />
        <Kpi label="Unmarked" value={fmtN(t.unmarked)} sub={t.unmarked ? "treated as present" : "all marked"} tone={t.unmarked ? "slate" : "emerald"} />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap bg-white border border-slate-200 rounded-sm px-2 py-1.5" data-testid="attendance-toolbar">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter employees…" className="h-7 text-[11.5px] px-2 border border-slate-200 rounded-sm w-44" data-testid="attendance-filter" />
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => bulk("mark_present_unmarked")} data-testid="attendance-mark-present"><CheckSquare size={12} className="mr-1" /> Mark unmarked as Present</Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setSitesOpen(true)} data-testid="attendance-sites-btn"><MapPin size={12} className="mr-1" /> Punch Sites</Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setHolidaysOpen(true)} data-testid="attendance-holidays-btn"><CalendarBlank size={12} className="mr-1" /> Holidays ({reg?.holidays?.length || 0})</Button>
        <Button size="sm" variant="outline" className={`h-7 text-[11px] ${pendingLeave ? "border-[#EA580C] text-[#EA580C]" : ""}`} onClick={() => setLeaveOpen(true)} data-testid="attendance-leave-btn">
          <CalendarCheck size={12} className="mr-1" /> Leave Requests{pendingLeave ? ` (${pendingLeave})` : ""}
        </Button>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} data-testid="attendance-upload-input" />
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="attendance-upload-btn"><Upload size={12} className="mr-1" /> Upload Excel</Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => downloadWithToast(`${base}/template.xlsx`, `Attendance_Template_${tag}.xlsx`).catch(() => {})} data-testid="attendance-template-btn"><DownloadSimple size={12} className="mr-1" /> Template</Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => downloadWithToast(`${base}/export.xlsx`, `Attendance_Register_${tag}.xlsx`).catch(() => {})} data-testid="attendance-export-btn"><FileXls size={12} className="mr-1" /> Excel</Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => downloadWithToast(`${base}/register.pdf`, `Attendance_Register_${tag}.pdf`).catch(() => {})} data-testid="attendance-pdf-btn"><FilePdf size={12} className="mr-1" /> Register PDF</Button>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={load} data-testid="attendance-refresh"><ArrowsClockwise size={12} /></Button>
        <Button size="sm" className="h-7 text-[11px] bg-[#EA580C] hover:bg-[#c94d0a] text-white" onClick={applyToRun} data-testid="attendance-sync-btn">
          <ArrowSquareOut size={12} className="mr-1" /> Sync to Payroll {reg?.run ? `(${reg.run.status})` : "(no run)"}
        </Button>
      </div>

      <div className="border border-slate-200 rounded-sm bg-white overflow-auto max-h-[calc(100vh-330px)]" data-testid="attendance-grid">
        <table className="text-[10.5px] border-collapse min-w-max">
          <thead className="sticky top-0 z-20">
            <tr className="bg-[#0F172A]">
              <th className="sticky left-0 z-30 bg-[#0F172A] text-left px-2 py-1.5 min-w-[190px] text-white/90 font-semibold">Employee</th>
              {dayHeads.map((h) => (
                <th key={h.d} className={`px-0 py-1 text-center min-w-[36px] font-semibold ${h.off ? "bg-[#1E293B] text-[#EA580C]" : h.holiday ? "bg-sky-900 text-sky-200" : "text-white/90"}`} title={h.holiday || ""}>
                  <div className="font-mono-num text-[10.5px]">{h.d}</div>
                  <div className="text-[8.5px] font-normal opacity-80">{h.wd}</div>
                </th>
              ))}
              {["Paid", "Lv", "Late", "HD", "Hrs"].map((s) => <th key={s} className="px-1 py-1 text-center min-w-[38px] text-white/90 font-semibold bg-[#EA580C]/90">{s}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={dim + 6} className="text-center text-slate-400 py-10">{busy ? "Loading…" : "No active employees — add them in Employee Master or import a salary register."}</td></tr>}
            {rows.map(({ e, idx }) => (
              <tr key={e.employee.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`attendance-row-${e.employee.emp_code}`}>
                <td className="sticky left-0 z-10 bg-white px-2 py-1 border-r border-slate-200">
                  <div className="font-semibold text-slate-900 leading-tight truncate max-w-[180px]">{e.employee.name}</div>
                  <div className="text-[9.5px] text-slate-500 font-mono-num">{e.employee.emp_code}{e.employee.designation ? ` · ${e.employee.designation}` : ""}</div>
                </td>
                {e.days.map((d) => <Cell key={d.day} d={d} active={editing?.empIdx === idx && editing?.day === d.day} onClick={(ev) => setEditing({ empIdx: idx, day: d.day, rect: ev.currentTarget.getBoundingClientRect() })} empCode={e.employee.emp_code} />)}
                <td className={`text-center font-mono-num font-bold px-1 ${e.summary.lop_days > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid={`attendance-paid-${e.employee.emp_code}`}>{fmtN(e.summary.paid_days)}</td>
                <td className="text-center font-mono-num px-1">{fmtN(e.summary.leaves)}</td>
                <td className={`text-center font-mono-num px-1 ${e.summary.late_marks ? "text-amber-700 font-semibold" : ""}`}>{fmtN(e.summary.late_marks)}</td>
                <td className="text-center font-mono-num px-1">{fmtN(e.summary.half_days)}</td>
                <td className="text-center font-mono-num px-1 text-slate-500">{e.summary.total_hours ? fmtN(e.summary.total_hours) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 flex-wrap text-[10.5px] text-slate-600" data-testid="attendance-legend">
        {(reg?.statuses || []).map((s) => <span key={s.code} className="flex items-center gap-1"><span className={`inline-block w-7 text-center rounded-sm border text-[9.5px] font-bold ${STATUS_STYLE[s.code]}`}>{s.code}</span>{s.label}{s.pay_value < 1 ? ` (${s.pay_value ? "½ pay" : "LOP"})` : ""}</span>)}
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-[#EA580C]" /> late beyond grace → half-day</span>
        <span className="flex items-center gap-1"><span className="inline-block w-7 text-center rounded-sm border border-dashed border-slate-300 text-[9.5px] text-slate-400">·</span> unmarked (counts as present)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-sky-500" /> self-punched (mobile)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-rose-500" /> punched outside office</span>
      </div>

      {editing && reg && (
        <AttendanceCellEditor
          key={`${editing.empIdx}-${editing.day}`}
          rect={editing.rect}
          employee={reg.employees[editing.empIdx].employee}
          day={reg.employees[editing.empIdx].days[editing.day - 1]}
          settings={reg.settings}
          base={base}
          onSaved={(payload) => onDaySaved(editing.empIdx, payload)}
          onClose={() => setEditing(null)}
          onMove={(delta) => {
            const nd = editing.day + delta;
            if (nd < 1 || nd > dim) return;
            setEditing({ ...editing, day: nd, rect: { ...editing.rect, left: editing.rect.left + delta * editing.rect.width } });
          }}
        />
      )}
      {holidaysOpen && <HolidaysDialog year={ym.year} onClose={() => { setHolidaysOpen(false); load(); }} />}
      {sitesOpen && <SitesDialog onClose={() => setSitesOpen(false)} />}
      {leaveOpen && <LeaveRequestsDialog onClose={() => { setLeaveOpen(false); loadPendingLeave(); }} onChanged={() => { load(); loadPendingLeave(); }} />}
    </div>
  );
}

function Cell({ d, active, onClick, empCode }) {
  const st = d.effective_status;
  const cls = d.unmarked ? "border-dashed border-slate-200 text-slate-300" : (STATUS_STYLE[st] || "");
  return (
    <td className="p-[2px]">
      <button
        onClick={onClick}
        title={`${d.date} · ${st}${d.in_time ? ` · In ${d.in_time}` : ""}${d.out_time ? ` · Out ${d.out_time}` : ""}${d.is_late ? ` · LATE ${d.late_min} min${d.forgiven ? " (forgiven)" : ""}` : ""}${d.remarks ? ` · ${d.remarks}` : ""}`}
        className={`relative w-[34px] h-[30px] rounded-sm border text-[10px] font-bold leading-none flex flex-col items-center justify-center transition-[transform,box-shadow] hover:-translate-y-px hover:shadow-sm ${cls} ${active ? "ring-2 ring-[#EA580C] ring-offset-1" : ""} ${(d.punch_in?.outside || d.punch_out?.outside) ? "ring-1 ring-rose-300" : ""}`}
        data-testid={`attendance-cell-${empCode}-${d.day}`}
      >
        <span>{d.unmarked ? "·" : st}</span>
        {d.in_time && <span className={`text-[7.5px] font-mono-num font-medium mt-0.5 ${d.is_late && !d.forgiven ? "text-[#EA580C]" : "opacity-70"}`}>{d.in_time}</span>}
        {d.is_late && <span className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${d.forgiven ? "bg-slate-400" : "bg-[#EA580C]"}`} />}
        {d.remarks && <span className="absolute bottom-0.5 left-0.5 w-1 h-1 rounded-full bg-slate-400" />}
        {d.source === "self" && <span className={`absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${(d.punch_in?.outside || d.punch_out?.outside) ? "bg-rose-500" : "bg-sky-500"}`} title="self-punched from mobile" />}
      </button>
    </td>
  );
}

function Kpi({ label, value, sub, tone }) {
  const toneCls = { rose: "text-rose-700", amber: "text-amber-700", emerald: "text-emerald-700", slate: "text-slate-500" }[tone] || "text-slate-900";
  return (
    <div className="border border-slate-200 bg-white rounded-sm px-3 py-2 hover:border-[#EA580C]/40 transition-colors" data-testid={`attendance-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
      <div className={`text-[16px] font-bold font-mono-num ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}
